import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import type { NewCommentReplyInboxItem } from '@seen/core/models/inbox'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { isOfType } from '@seen/core/utils'
import { cn, IconComment } from '@seen/ui'
import type { ReactNode } from 'react'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'
import { useCommentParentResource } from '#src/hooks/inbox/useCommentParentResource.ts'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useTimelineItemUrl } from '#src/hooks/useTimelineItemUrl.ts'
import { InboxRow } from '#src/pages/inbox/row/InboxRow.tsx'
import { InboxRowAvatar } from '#src/pages/inbox/row/InboxRowAvatar.tsx'
import { InboxRowContent } from '#src/pages/inbox/row/InboxRowContent.tsx'
import { InboxRowIcon } from '#src/pages/inbox/row/InboxRowIcon.tsx'
import { InboxRowSubtext } from '#src/pages/inbox/row/InboxRowSubtext.tsx'
import { InboxRowText } from '#src/pages/inbox/row/InboxRowText.tsx'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

export interface NewCommentReplyInboxItemRowProps {
  item: NewCommentReplyInboxItem
  notificationItem: NotificationItem
  onSelect?: () => void
  isSelected?: boolean
  sidebarOpen?: boolean
}

export function NewCommentReplyInboxItemRow({
  item,
  notificationItem,
  onSelect,
  isSelected,
  sidebarOpen,
}: NewCommentReplyInboxItemRowProps): ReactNode {
  const {
    comment,
    parentResourceType,
    relatedTask,
    relatedOrder,
    patient,
    isLoading,
  } = useCommentParentResource(item.comment)
  const { data: parentComment } = useRead(
    CommentCommunication,
    comment?.parentComment
  )

  const hasRelatedTask = parentResourceType === 'Task' || !!relatedTask
  const hasRelatedOrder =
    parentResourceType === 'ServiceRequest' || !!relatedOrder

  const { data: practitioner } = useRead(
    ExtendedPractitioner,
    comment?.practitionerSender
  )

  const commentUrl = useTimelineItemUrl(comment)

  const orderTitle = getOrderTitle(relatedOrder)

  return (
    <InboxRow
      to={commentUrl}
      notificationItem={notificationItem}
      onSelect={onSelect}
      isSelected={isSelected}
      sidebarOpen={sidebarOpen}
    >
      <InboxRowAvatar
        profile={comment?.practitionerSender}
        icon={
          <InboxRowIcon>
            <IconComment className="size-[0.6875rem]" />
          </InboxRowIcon>
        }
      />
      <InboxRowContent>
        <InboxRowText
          notificationItem={notificationItem}
          isLoading={isLoading}
          badges={
            <>
              {patient && (
                <ParticipantTaskRowBadge
                  className="[&>button]:h-[1.55rem] [&>button]:py-0"
                  participant={patient.reference}
                />
              )}
              {hasRelatedTask &&
                isOfType(relatedTask?.focus, 'ServiceRequest') &&
                relatedOrder && (
                  <RowBadge
                    loading={!relatedOrder}
                    icon={
                      <IconOrderStatus
                        status={relatedOrder?.status}
                        isSubmitting={relatedOrder?.isSubmitting}
                        className="h-[1.55rem] py-0"
                      />
                    }
                  >
                    {relatedOrder?.seenIdentifier} {!!orderTitle && orderTitle}
                  </RowBadge>
                )}
            </>
          }
        >
          {hasRelatedTask && (
            <>
              {relatedTask?.priority && (
                <TaskPriorityIcon
                  priority={relatedTask.priority}
                  isTaskCompletedOrCancelled={
                    relatedTask.isCompletedOrCancelled
                  }
                  className={cn('-mt-0.75 mr-0.75 inline')}
                />
              )}
              {relatedTask?.state && (
                <IconTaskState
                  state={relatedTask.state}
                  className="-mt-0.75 mr-0.75 inline"
                />
              )}
              <span>{relatedTask?.title ?? '[deleted]'}</span>
            </>
          )}
          {!hasRelatedTask && (
            <span>
              {extractTextFromHTML(comment?.message ?? '[deleted comment]')}
            </span>
          )}
        </InboxRowText>
        <InboxRowSubtext>
          {!!practitioner && (
            <>
              {practitioner?.name}
              {!!practitioner?.roles?.length && (
                <> {practitioner?.roles?.join(', ')}</>
              )}{' '}
              {!relatedTask && (
                <>
                  replied to &ldquo;
                  {extractTextFromHTML(parentComment?.message ?? '')}&rdquo;
                </>
              )}
              {!!relatedTask && (
                <>
                  replied:{' '}
                  {extractTextFromHTML(comment?.message ?? '[deleted comment]')}
                </>
              )}
            </>
          )}
          {!practitioner && (
            <>
              {!relatedTask && (
                <>
                  Reply to &ldquo;
                  {extractTextFromHTML(parentComment?.message ?? '')}
                  &rdquo;
                </>
              )}
              {!!relatedTask && (
                <>
                  Reply:{' '}
                  {extractTextFromHTML(comment?.message ?? '[deleted comment]')}
                </>
              )}
            </>
          )}
          {!hasRelatedTask &&
            !hasRelatedOrder &&
            patient?.name &&
            ` \u00b7 ${patient.name}`}
        </InboxRowSubtext>
      </InboxRowContent>
    </InboxRow>
  )
}
